// src/components/AdminRegister.js
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AdminRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(''); // Clear error on input change
  };

  const validateEmail = (email) => {
    return email.toLowerCase().endsWith('@tjgcpas.com');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Admin email must end with @tjgcpas.com');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API}/admin/register`,
        {
          name: formData.name,
          email: formData.email,
          password: formData.password
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Registration successful! Check your email for verification code.');
        navigate('/admin/verify', { 
          state: { 
            adminId: response.data.adminId, 
            email: response.data.email 
          } 
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={formBoxStyle}>
        <h2 style={titleStyle}>Admin Registration</h2>
        <p style={subtitleStyle}>Register with your @tjgcpas.com email</p>

        {error && <div style={errorBoxStyle}>{error}</div>}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              style={inputStyle}
              disabled={loading}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john.doe@tjgcpas.com"
              style={inputStyle}
              disabled={loading}
            />
            <small style={hintStyle}>Must end with @tjgcpas.com</small>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Minimum 6 characters"
              style={inputStyle}
              disabled={loading}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              style={inputStyle}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            style={{
              ...buttonStyle,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register Admin Account'}
          </button>
        </form>

        <div style={linkBoxStyle}>
          Already have an admin account?{' '}
          <a href="/admin/form/11" style={linkStyle}>
            Login here
          </a>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px'
};

const formBoxStyle = {
  background: 'white',
  borderRadius: '12px',
  padding: '40px',
  maxWidth: '450px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

const titleStyle = {
  margin: '0 0 8px 0',
  fontSize: '28px',
  fontWeight: '700',
  color: '#1f2937',
  textAlign: 'center'
};

const subtitleStyle = {
  margin: '0 0 24px 0',
  fontSize: '14px',
  color: '#6b7280',
  textAlign: 'center'
};

const errorBoxStyle = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  padding: '12px',
  borderRadius: '8px',
  marginBottom: '20px',
  fontSize: '14px'
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
};

const fieldGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const labelStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#374151'
};

const inputStyle = {
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '15px',
  outline: 'none',
  transition: 'border-color 0.2s'
};

const hintStyle = {
  fontSize: '12px',
  color: '#6b7280',
  fontStyle: 'italic'
};

const buttonStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  border: 'none',
  padding: '14px 24px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '600',
  marginTop: '8px'
};

const linkBoxStyle = {
  marginTop: '24px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#6b7280'
};

const linkStyle = {
  color: '#667eea',
  fontWeight: '600',
  textDecoration: 'none'
};

export default AdminRegister;








// // src/components/AdminRegister.js
// import React, { useState } from 'react';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';

// const AdminRegister = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     name: '',
//     email: '',
//     password: '',
//     confirmPassword: ''
//   });
//   const [error, setError] = useState('');
//   const [loading, setLoading] = useState(false);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//     setError(''); // Clear error on input change
//   };

//   const validateEmail = (email) => {
//     return email.toLowerCase().endsWith('@tjgcpas.com');
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError('');

//     // Validation
//     if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
//       setError('All fields are required');
//       return;
//     }

//     if (!validateEmail(formData.email)) {
//       setError('Admin email must end with @tjgcpas.com');
//       return;
//     }

//     if (formData.password.length < 6) {
//       setError('Password must be at least 6 characters');
//       return;
//     }

//     if (formData.password !== formData.confirmPassword) {
//       setError('Passwords do not match');
//       return;
//     }

//     try {
//       setLoading(true);
//       const response = await axios.post(
//         `${process.env.REACT_APP_API || 'http://localhost:8000'}/admin/register`,
//         {
//           name: formData.name,
//           email: formData.email,
//           password: formData.password
//         },
//         { withCredentials: true }
//       );

//       if (response.data.success) {
//         alert('Admin registration successful! Please login.');
//         navigate('/admin/form/11'); // Navigate to admin login
//       }
//     } catch (err) {
//       setError(err.response?.data?.message || 'Registration failed. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div style={containerStyle}>
//       <div style={formBoxStyle}>
//         <h2 style={titleStyle}>Admin Registration</h2>
//         <p style={subtitleStyle}>Register with your @tjgcpas.com email</p>

//         {error && <div style={errorBoxStyle}>{error}</div>}

//         <form onSubmit={handleSubmit} style={formStyle}>
//           <div style={fieldGroupStyle}>
//             <label style={labelStyle}>Full Name</label>
//             <input
//               type="text"
//               name="name"
//               value={formData.name}
//               onChange={handleChange}
//               placeholder="John Doe"
//               style={inputStyle}
//               disabled={loading}
//             />
//           </div>

//           <div style={fieldGroupStyle}>
//             <label style={labelStyle}>Email Address</label>
//             <input
//               type="email"
//               name="email"
//               value={formData.email}
//               onChange={handleChange}
//               placeholder="john.doe@tjgcpas.com"
//               style={inputStyle}
//               disabled={loading}
//             />
//             <small style={hintStyle}>Must end with @tjgcpas.com</small>
//           </div>

//           <div style={fieldGroupStyle}>
//             <label style={labelStyle}>Password</label>
//             <input
//               type="password"
//               name="password"
//               value={formData.password}
//               onChange={handleChange}
//               placeholder="Minimum 6 characters"
//               style={inputStyle}
//               disabled={loading}
//             />
//           </div>

//           <div style={fieldGroupStyle}>
//             <label style={labelStyle}>Confirm Password</label>
//             <input
//               type="password"
//               name="confirmPassword"
//               value={formData.confirmPassword}
//               onChange={handleChange}
//               placeholder="Re-enter password"
//               style={inputStyle}
//               disabled={loading}
//             />
//           </div>

//           <button
//             type="submit"
//             style={{
//               ...buttonStyle,
//               opacity: loading ? 0.6 : 1,
//               cursor: loading ? 'not-allowed' : 'pointer'
//             }}
//             disabled={loading}
//           >
//             {loading ? 'Registering...' : 'Register Admin Account'}
//           </button>
//         </form>

//         <div style={linkBoxStyle}>
//           Already have an admin account?{' '}
//           <a href="/admin/form/11" style={linkStyle}>
//             Login here
//           </a>
//         </div>
//       </div>
//     </div>
//   );
// };

// // Styles
// const containerStyle = {
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
//   minHeight: '100vh',
//   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//   padding: '20px'
// };

// const formBoxStyle = {
//   background: 'white',
//   borderRadius: '12px',
//   padding: '40px',
//   maxWidth: '450px',
//   width: '100%',
//   boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
// };

// const titleStyle = {
//   margin: '0 0 8px 0',
//   fontSize: '28px',
//   fontWeight: '700',
//   color: '#1f2937',
//   textAlign: 'center'
// };

// const subtitleStyle = {
//   margin: '0 0 24px 0',
//   fontSize: '14px',
//   color: '#6b7280',
//   textAlign: 'center'
// };

// const errorBoxStyle = {
//   background: '#fef2f2',
//   border: '1px solid #fecaca',
//   color: '#991b1b',
//   padding: '12px',
//   borderRadius: '8px',
//   marginBottom: '20px',
//   fontSize: '14px'
// };

// const formStyle = {
//   display: 'flex',
//   flexDirection: 'column',
//   gap: '20px'
// };

// const fieldGroupStyle = {
//   display: 'flex',
//   flexDirection: 'column',
//   gap: '6px'
// };

// const labelStyle = {
//   fontSize: '14px',
//   fontWeight: '600',
//   color: '#374151'
// };

// const inputStyle = {
//   padding: '12px 16px',
//   border: '1px solid #d1d5db',
//   borderRadius: '8px',
//   fontSize: '15px',
//   outline: 'none',
//   transition: 'border-color 0.2s'
// };

// const hintStyle = {
//   fontSize: '12px',
//   color: '#6b7280',
//   fontStyle: 'italic'
// };

// const buttonStyle = {
//   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//   color: 'white',
//   border: 'none',
//   padding: '14px 24px',
//   borderRadius: '8px',
//   fontSize: '16px',
//   fontWeight: '600',
//   marginTop: '8px'
// };

// const linkBoxStyle = {
//   marginTop: '24px',
//   textAlign: 'center',
//   fontSize: '14px',
//   color: '#6b7280'
// };

// const linkStyle = {
//   color: '#667eea',
//   fontWeight: '600',
//   textDecoration: 'none'
// };

// export default AdminRegister;